// @ts-nocheck There is an error with @atlaskit/dynamic-table props
import React, { MutableRefObject } from 'react';
// eslint-disable-next-line import/no-unresolved
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipProps } from 'recharts';
import SectionMessage from '@atlaskit/section-message';

import { DataPointsEventsTotals } from '@/ui';
import ItemHeader from '@/components/primitive/item-header';
import {
  alignItemsStart,
  positionRelative,
  flex,
  flexDirectionColumn,
  maxContent,
  spaceTopComfortable,
  card,
  spaceTopCozy,
} from '@/styles';
import { selectedDateLabels } from '@/utils/date';
import { localNumberFormat } from '@/utils/string';

import { formatPerformanceResult, formatErrorBudget, getDailyPerformance } from '../utils';

import EventsList from './tooltip/events-list';
import NoResultsAvailable from './tooltip/no-result-available';
import RightAlign from './tooltip/right-align';
import IncidentsList from './tooltip/incidents-list';
import { lowEmphasisText, tooltipBackground, tooltipResults } from './tooltip/styles';

export type CustomToolTipProps = TooltipProps<any, any> & {
  sticky?: boolean;
  showClickPrompt?: boolean;
};

const CustomTooltip = ({ active, payload = [], showClickPrompt }: CustomToolTipProps) => {
  if (active && payload && payload.length) {
    const {
      result,
      rollup,
      timestamp,
      goodCount,
      badCount,
      dailyGood,
      dailyBad,
      events: allEvents = [],
      incidents = [],
      isHighCardinality,
    } = payload[0].payload as DataPointsEventsTotals & { rollup: number };
    const { timestampLabel, rangeLabel } = selectedDateLabels(timestamp);
    const dailyPerformance = getDailyPerformance(dailyGood, dailyBad);
    const hasEventCounts = goodCount !== undefined || badCount !== undefined;
    const incidentsCount = incidents.length;

    return (
      <div
        css={[positionRelative, flex, flexDirectionColumn, alignItemsStart, maxContent]}
        onClick={(e: any) => e.stopPropagation()}
      >
        {hasEventCounts && (
          <div css={[tooltipBackground, card]}>
            <h4>{timestampLabel}</h4>
            <div css={[tooltipResults]}>
              {goodCount === undefined && badCount === undefined && <NoResultsAvailable />}
              {(result || result === 0) && (
                <>
                  <ItemHeader>Performance</ItemHeader>
                  <RightAlign>{formatPerformanceResult(dailyPerformance)}%</RightAlign>
                  <ItemHeader>Good Events</ItemHeader>
                  <RightAlign>{localNumberFormat(dailyGood)}</RightAlign>
                  <ItemHeader>Bad Events</ItemHeader>
                  <RightAlign>{localNumberFormat(dailyBad)}</RightAlign>
                </>
              )}
            </div>
            <h4>{rangeLabel}</h4>
            <div css={[tooltipResults]}>
              <ItemHeader>Performance</ItemHeader>
              <RightAlign>{formatPerformanceResult(rollup)}%</RightAlign>
              <ItemHeader>Error Budget</ItemHeader>
              <RightAlign>{formatErrorBudget(result, { truncate: false })}%</RightAlign>
              <ItemHeader>Good Events</ItemHeader>
              <RightAlign>{localNumberFormat(goodCount)}</RightAlign>
              <ItemHeader>Bad Events</ItemHeader>
              <RightAlign>{localNumberFormat(badCount)}</RightAlign>
            </div>
            <IncidentsList incidentsCount={incidentsCount} />
            <EventsList events={allEvents} />
            {isHighCardinality && !!allEvents.length && (
              <div css={[spaceTopCozy]}>
                <SectionMessage appearance="warning" actions={[]}>
                  <p>High Cardinality Metrics</p>
                </SectionMessage>
              </div>
            )}
            {showClickPrompt && allEvents.length > 0 && (
              <div css={[spaceTopComfortable]}>
                <p css={[lowEmphasisText]}>Click the graph to view event details</p>
              </div>
            )}
          </div>
        )}
        {!hasEventCounts && (
          <div css={[tooltipBackground, card]}>
            <h4>{timestampLabel}</h4>
            <div css={[tooltipResults]}>
              {goodCount === undefined && badCount === undefined && <NoResultsAvailable />}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export type StickyCustomToolTipProps = CustomToolTipProps & {
  stickyPayloadRef: MutableRefObject<Array<Payload<number, string>>>;
};

// This tooltip wrapper caches the last selected payload to a provided
// reference. This can be read to determine which day of the graph is selected
// when clicked.
// If sticky is set to true, the tooltip is rendered with a snapshot of the
// last captured payload value until sticky is unset.
export const StickyCustomTooltip = ({
  active,
  payload = [],
  showClickPrompt,
  sticky = undefined,
  stickyPayloadRef,
}: StickyCustomToolTipProps) => {
  stickyPayloadRef.current = payload;

  if (sticky !== undefined && !sticky) {
    stickyPayloadRef.current = payload;
    return <CustomTooltip active={active} payload={payload} showClickPrompt={showClickPrompt} />;
  }
  return (
    <CustomTooltip
      active
      payload={stickyPayloadRef.current}
      sticky={sticky}
      showClickPrompt={showClickPrompt}
    />
  );
};

export default CustomTooltip;
